export const IMPERSONATION_STORAGE_KEY = "dental_os_impersonation_session_id";
export const IMPERSONATION_ACCESS_TOKEN_KEY = "dental_os_impersonation_access_token";
export const IMPERSONATION_ADMIN_RETURN_PATH_KEY = "dental_os_impersonation_admin_return_path";
export const IMPERSONATION_SESSION_CONTEXT_KEY = "dental_os_impersonation_session_context";
const CLINIC_APP_BASE_URL = process.env.REACT_APP_CLINIC_APP_URL || window.location.origin;
const LOCAL_SUPER_ADMIN_URL = "http://127.0.0.1:3030";
const PRODUCTION_SUPER_ADMIN_URL = "https://admin.dental-os.app";

function cleanSessionId(value) {
  const cleaned = String(value || "").trim();
  return cleaned || null;
}

function cleanReturnPath(value) {
  const cleaned = String(value || "").trim();
  if (!cleaned || !cleaned.startsWith("/") || cleaned.startsWith("//")) return "/";
  return cleaned;
}

function cleanText(value) {
  const cleaned = String(value || "").trim();
  return cleaned || null;
}

function defaultSuperAdminBaseUrl() {
  const configured = String(process.env.REACT_APP_SUPER_ADMIN_URL || "").trim();
  if (configured) return configured;
  const hostname = typeof window !== "undefined" ? window.location?.hostname : "";
  if (["localhost", "127.0.0.1", "::1"].includes(hostname)) return LOCAL_SUPER_ADMIN_URL;
  return PRODUCTION_SUPER_ADMIN_URL;
}

export function getStoredImpersonationSessionId() {
  if (typeof window === "undefined") return null;
  return cleanSessionId(window.localStorage.getItem(IMPERSONATION_STORAGE_KEY));
}

export function setStoredImpersonationSessionId(sessionId) {
  if (typeof window === "undefined") return;
  const cleaned = cleanSessionId(sessionId);
  if (cleaned) window.localStorage.setItem(IMPERSONATION_STORAGE_KEY, cleaned);
}

export function getStoredImpersonationAccessToken() {
  if (typeof window === "undefined") return null;
  return cleanSessionId(window.localStorage.getItem(IMPERSONATION_ACCESS_TOKEN_KEY));
}

export function setStoredImpersonationAuthTokens(message) {
  if (typeof window === "undefined") return;
  const accessToken = cleanSessionId(message?.access_token);
  if (accessToken) window.localStorage.setItem(IMPERSONATION_ACCESS_TOKEN_KEY, accessToken);
}

export function getStoredImpersonationSessionContext() {
  if (typeof window === "undefined") return null;
  const sessionId = getStoredImpersonationSessionId();
  if (!sessionId) return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(IMPERSONATION_SESSION_CONTEXT_KEY) || "null");
    if (!parsed || cleanSessionId(parsed.id) !== sessionId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setStoredImpersonationSessionContext(session) {
  if (typeof window === "undefined") return;
  const id = cleanSessionId(session?.id || session?.session_id);
  const targetUserId = cleanSessionId(session?.target_user_id);
  if (!id || !targetUserId) {
    window.localStorage.removeItem(IMPERSONATION_SESSION_CONTEXT_KEY);
    return;
  }
  window.localStorage.setItem(IMPERSONATION_SESSION_CONTEXT_KEY, JSON.stringify({
    id,
    target_user_id: targetUserId,
    target_user_email: cleanText(session?.target_user_email),
    target_user_name: cleanText(session?.target_user_name),
    target_user_role: cleanText(session?.target_user_role),
    clinic_id: cleanSessionId(session?.clinic_id),
    clinic_name: cleanText(session?.clinic_name),
  }));
}

export function getStoredImpersonationAdminReturnPath() {
  if (typeof window === "undefined") return "/";
  return cleanReturnPath(window.localStorage.getItem(IMPERSONATION_ADMIN_RETURN_PATH_KEY));
}

export function setStoredImpersonationAdminReturnPath(path) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(IMPERSONATION_ADMIN_RETURN_PATH_KEY, cleanReturnPath(path));
}

export function getImpersonationAdminReturnUrl(baseUrl = defaultSuperAdminBaseUrl()) {
  const base = String(baseUrl || defaultSuperAdminBaseUrl()).replace(/\/+$/, "");
  return new URL(getStoredImpersonationAdminReturnPath(), base).toString();
}

export function clearStoredImpersonationSessionId() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  window.localStorage.removeItem(IMPERSONATION_ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(IMPERSONATION_ADMIN_RETURN_PATH_KEY);
  window.localStorage.removeItem(IMPERSONATION_SESSION_CONTEXT_KEY);
}

export function buildImpersonationUrl(baseUrl = CLINIC_APP_BASE_URL, sessionId) {
  const url = new URL("/impersonation", String(baseUrl || window.location.origin).replace(/\/+$/, ""));
  url.searchParams.set("session_id", cleanSessionId(sessionId) || "");
  return url.toString();
}
